import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, TextField, Button, Table, TableHead, TableBody,
  TableRow, TableCell, Chip, CircularProgress, Alert, IconButton,
  Divider, FormControlLabel, Checkbox, Tooltip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, InfoOutlined as InfoIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { createSprint, updateSprint, getSprint, getSprints, getVelocity, getTeam } from './services/capacityApi';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

/** Count Mon–Fri weekdays between two ISO date strings (inclusive). */
function countBusinessDays(startStr, endStr) {
  if (!startStr || !endStr) return 0;
  try {
    const start = new Date(startStr + 'T00:00:00');
    const end = new Date(endStr + 'T00:00:00');
    if (end < start) return 0;
    let count = 0;
    const d = new Date(start);
    while (d <= end) {
      const day = d.getDay();
      if (day !== 0 && day !== 6) count++;
      d.setDate(d.getDate() + 1);
    }
    return count;
  } catch {
    return 0;
  }
}

/** Returns the unique months (0-based index) covered by the sprint date range. */
function getSprintMonthIndices(startStr, endStr) {
  if (!startStr || !endStr) return [];
  try {
    const start = new Date(startStr + 'T00:00:00');
    const end = new Date(endStr + 'T00:00:00');
    const months = [];
    const d = new Date(start.getFullYear(), start.getMonth(), 1);
    while (d <= end) {
      months.push(d.getMonth()); // 0-based
      d.setMonth(d.getMonth() + 1);
    }
    return months;
  } catch {
    return [];
  }
}

/**
 * Given flat allocation records (from /api/outcome-allocations/{year}),
 * compute a member's allocation % to the team's linked outcomes for the
 * months covered by the sprint.
 *
 * Returns: { pct: number, breakdown: [{outcome_name, pct}], hasData: boolean }
 */
function computeMemberAllocation(memberId, teamOutcomeIds, allocationRecords, sprintMonthIndices) {
  if (!teamOutcomeIds || teamOutcomeIds.length === 0 || sprintMonthIndices.length === 0) {
    return { pct: 100, breakdown: [], hasData: false };
  }

  const relevant = allocationRecords.filter(
    (a) => teamOutcomeIds.includes(a.outcome_id) && a.member_id === memberId
  );

  if (relevant.length === 0) {
    return { pct: 100, breakdown: [], hasData: false };
  }

  // For each sprint month, sum allocations across all linked outcomes
  const perMonthTotals = sprintMonthIndices.map((monthIdx) => {
    let total = 0;
    const breakdown = [];
    for (const alloc of relevant) {
      const pct = (alloc.monthly_allocations || [])[monthIdx] || 0;
      if (pct > 0) {
        // Merge duplicate outcome names (e.g. multiple milestones in same outcome)
        const existing = breakdown.find((b) => b.outcome_name === alloc.outcome_name);
        if (existing) {
          existing.pct = Math.min(existing.pct + pct, 100);
        } else {
          breakdown.push({ outcome_name: alloc.outcome_name, pct });
        }
        total += pct;
      }
    }
    return { total: Math.min(total, 100), breakdown };
  });

  // Simple average across sprint months
  const avgPct = perMonthTotals.reduce((s, m) => s + m.total, 0) / perMonthTotals.length;

  // Use first month's breakdown for the tooltip (most relevant)
  const breakdown = perMonthTotals[0]?.breakdown || [];

  return { pct: avgPct, breakdown, hasData: true };
}

/** Build a human-readable tooltip string for the available days calculation. */
function buildTooltip(businessDays, allocation, isOverride, autoPct) {
  const lines = [];
  if (isOverride) {
    lines.push(`⚠ Manually overridden (auto: ${autoPct != null ? autoPct.toFixed(0) : '?'}%)`);
    lines.push('');
  }
  if (!allocation.hasData && !isOverride) {
    return `${businessDays} business days\n(No allocation data found for linked outcomes — using 100%)`;
  }
  lines.push(`${businessDays} business days × ${allocation.pct.toFixed(0)}% allocation`);
  if (!isOverride && allocation.breakdown.length > 0) {
    lines.push('');
    lines.push('Allocation breakdown:');
    for (const b of allocation.breakdown) {
      lines.push(`  • ${b.outcome_name}: ${b.pct.toFixed(0)}%`);
    }
  }
  lines.push('');
  const avail = (businessDays * allocation.pct / 100).toFixed(1);
  lines.push(`= ${avail} available days`);
  return lines.join('\n');
}

export default function SprintForm({ teamId, sprintId, onSaved, onCancel, isReadOnly }) {
  const isEdit = Boolean(sprintId);

  const [sprintName, setSprintName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [memberRows, setMemberRows] = useState([]);
  const [actualSpDev, setActualSpDev] = useState('');
  const [actualSpQa, setActualSpQa] = useState('');
  const [markCompleted, setMarkCompleted] = useState(false);

  const [velocity, setVelocity] = useState(null);
  const [teamOutcomeIds, setTeamOutcomeIds] = useState([]);
  const [allocationRecords, setAllocationRecords] = useState([]); // flat list from API
  const [existingSprints, setExistingSprints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // ── Derived values ──────────────────────────────────────────────────────────

  const businessDays = useMemo(() => countBusinessDays(startDate, endDate), [startDate, endDate]);
  const sprintMonthIndices = useMemo(() => getSprintMonthIndices(startDate, endDate), [startDate, endDate]);

  /** Per-member allocation computation — rerun when dates or allocation data changes. */
  const memberAllocations = useMemo(() => {
    const map = new Map();
    for (const row of memberRows) {
      const alloc = computeMemberAllocation(
        row.roster_member_id,
        teamOutcomeIds,
        allocationRecords,
        sprintMonthIndices
      );
      map.set(row.roster_member_id, alloc);
    }
    return map;
  }, [memberRows, teamOutcomeIds, allocationRecords, sprintMonthIndices]);

  /** Recompute available_days whenever dates or allocations change. Skips manually overridden rows. */
  useEffect(() => {
    setMemberRows((rows) => {
      const next = rows.map((r) => {
        if (r.archived_at_sprint) return r;
        if (r._allocationOverride) {
          const newAvail = parseFloat((businessDays * r.allocation_pct / 100).toFixed(1));
          const newPlanned = r._wasDefault
            ? Math.floor(newAvail)
            : Math.min(r.planned_days, Math.ceil(newAvail));
          if (r.available_days === newAvail && r.planned_days === newPlanned) return r;
          return { ...r, available_days: newAvail, planned_days: newPlanned };
        }
        const alloc = memberAllocations.get(r.roster_member_id) || { pct: 100, hasData: false };
        const newAvail = parseFloat((businessDays * alloc.pct / 100).toFixed(1));
        const newPlanned = r._wasDefault
          ? Math.floor(newAvail)
          : Math.min(r.planned_days, Math.ceil(newAvail));
        if (r.available_days === newAvail && r.planned_days === newPlanned && r.allocation_pct === alloc.pct) return r;
        return { ...r, available_days: newAvail, planned_days: newPlanned, allocation_pct: alloc.pct };
      });
      if (next.every((r, i) => r === rows[i])) return rows; // nothing changed — same reference, no re-render
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessDays, memberAllocations]);

  // ── Initial load ─────────────────────────────────────────────────────────────

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [vel, teamData, sprintsRes] = await Promise.all([
          getVelocity(teamId),
          getTeam(teamId),
          getSprints(teamId),
        ]);
        setVelocity(vel);
        setExistingSprints(sprintsRes?.sprints || []);
        const outcomeIds = teamData?.outcome_ids || [];
        setTeamOutcomeIds(outcomeIds);

        // Fetch allocation data for the relevant year (default to current year)
        const year = startDate ? new Date(startDate + 'T00:00:00').getFullYear() : new Date().getFullYear();
        fetchAllocationData(year);

        if (isEdit) {
          const sprint = await getSprint(teamId, sprintId);
          setSprintName(sprint.name || '');
          setStartDate(sprint.start_date || '');
          setEndDate(sprint.end_date || '');
          setMemberRows((sprint.member_days || []).map((md) => ({ ...md, member_type: md.member_type || 'dev', _wasDefault: false })));
          setActualSpDev(sprint.actual_sp_dev != null ? String(sprint.actual_sp_dev) : '');
          setActualSpQa(sprint.actual_sp_qa != null ? String(sprint.actual_sp_qa) : '');
          setMarkCompleted(sprint.status === 'completed');
        } else {
          const activeMembers = (teamData?.members || []).filter((m) => !m.archived);
          setMemberRows(
            activeMembers.map((m) => ({
              roster_member_id: m.roster_member_id,
              member_name: m.member_name || '',
              member_type: m.member_type || 'dev',
              available_days: 0,
              planned_days: 0,
              allocation_pct: 100,
              archived_at_sprint: false,
              _wasDefault: true,
            }))
          );
        }
      } catch (e) {
        setError('Failed to load sprint data');
      } finally {
        setLoading(false);
      }
    }
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, sprintId, isEdit]);

  // Re-fetch allocation data when start year changes
  useEffect(() => {
    if (!startDate) return;
    const year = new Date(startDate + 'T00:00:00').getFullYear();
    fetchAllocationData(year);
  }, [startDate]);

  async function fetchAllocationData(year) {
    try {
      const res = await fetch(`${API_BASE}/api/outcome-allocations/${year}`);
      if (res.ok) {
        const data = await res.json();
        setAllocationRecords(Array.isArray(data) ? data : []);
      }
    } catch {
      // Non-critical — allocation display degrades gracefully
    }
  }

  // ── Derived sprint totals ──────────────────────────────────────────────────

  const totalDevDays = useMemo(
    () => memberRows.filter((r) => !r.archived_at_sprint && (r.member_type || 'dev') === 'dev').reduce((sum, r) => sum + (r.planned_days || 0), 0),
    [memberRows]
  );

  const totalQaDays = useMemo(
    () => memberRows.filter((r) => !r.archived_at_sprint && r.member_type === 'qa').reduce((sum, r) => sum + (r.planned_days || 0), 0),
    [memberRows]
  );

  const expectedSpDev = useMemo(
    () => velocity?.dev ? (totalDevDays * velocity.dev.rate).toFixed(1) : '—',
    [totalDevDays, velocity]
  );

  const expectedSpQa = useMemo(
    () => velocity?.qa ? (totalQaDays * velocity.qa.rate).toFixed(1) : '—',
    [totalQaDays, velocity]
  );

  const sortedMemberRows = useMemo(
    () => [...memberRows].sort((a, b) => {
      const aIsQa = a.member_type === 'qa' ? 1 : 0;
      const bIsQa = b.member_type === 'qa' ? 1 : 0;
      return aIsQa - bIsQa;
    }),
    [memberRows]
  );

  const overlapWarning = useMemo(() => {
    if (!startDate || !endDate) return null;
    const others = existingSprints.filter((s) => s.id !== sprintId);
    const hit = others.find((s) => startDate <= s.end_date && endDate >= s.start_date);
    if (!hit) return null;
    return `Dates overlap with "${hit.name}" (${hit.start_date} – ${hit.end_date})`;
  }, [startDate, endDate, existingSprints, sprintId]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  function updateAllocationOverride(rosterId, rawValue) {
    const pct = Math.max(0, Math.min(100, parseFloat(rawValue) || 0));
    setMemberRows((rows) =>
      rows.map((r) => {
        if (r.roster_member_id !== rosterId) return r;
        const newAvail = parseFloat((businessDays * pct / 100).toFixed(1));
        const newPlanned = r._wasDefault
          ? Math.floor(newAvail)
          : Math.min(r.planned_days, Math.ceil(newAvail));
        return { ...r, allocation_pct: pct, available_days: newAvail, planned_days: newPlanned, _allocationOverride: true };
      })
    );
  }

  function resetAllocationOverride(rosterId) {
    const alloc = memberAllocations.get(rosterId) || { pct: 100, hasData: false };
    const newAvail = parseFloat((businessDays * alloc.pct / 100).toFixed(1));
    setMemberRows((rows) =>
      rows.map((r) => {
        if (r.roster_member_id !== rosterId) return r;
        const newPlanned = r._wasDefault
          ? Math.floor(newAvail)
          : Math.min(r.planned_days, Math.ceil(newAvail));
        return { ...r, allocation_pct: alloc.pct, available_days: newAvail, planned_days: newPlanned, _allocationOverride: false };
      })
    );
  }

  function updateRow(rosterId, field, value) {
    setMemberRows((rows) =>
      rows.map((r) =>
        r.roster_member_id === rosterId ? { ...r, [field]: value, _wasDefault: false } : r
      )
    );
  }

  async function handleSave() {
    if (!sprintName.trim()) { setError('Sprint name is required'); return; }
    if (!startDate || !endDate) { setError('Start and end dates are required'); return; }
    if (endDate < startDate) { setError('End date must be on or after start date'); return; }

    for (const r of memberRows) {
      if (!r.archived_at_sprint && r.planned_days > Math.ceil(r.available_days)) {
        setError(`${r.member_name}: planned days (${r.planned_days}) exceeds available days (${r.available_days})`);
        return;
      }
    }

    setSaving(true);
    setError(null);
    try {
      const payload = {
        name: sprintName.trim(),
        start_date: startDate,
        end_date: endDate,
        member_days: memberRows.map((r) => ({
          roster_member_id: r.roster_member_id,
          planned_days: r.planned_days,
          available_days: r.available_days,
          allocation_pct: r.allocation_pct,
        })),
      };
      if (markCompleted) {
        payload.status = 'completed';
        const devSp = actualSpDev !== '' ? parseFloat(actualSpDev) || 0 : 0;
        const qaSp = actualSpQa !== '' ? parseFloat(actualSpQa) || 0 : 0;
        payload.actual_sp_dev = devSp;
        payload.actual_sp_qa = qaSp;
        payload.actual_sp = devSp + qaSp;
      }

      const result = isEdit
        ? await updateSprint(teamId, sprintId, payload)
        : await createSprint(teamId, payload);
      onSaved(result);
    } catch (e) {
      setError(e?.message || 'Failed to save sprint');
    } finally {
      setSaving(false);
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onCancel} size="small">Team</Button>
          <Typography variant="h6">{isEdit ? 'Edit Sprint' : 'New Sprint'}</Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <TextField
            label="Sprint Name"
            value={sprintName}
            onChange={(e) => setSprintName(e.target.value)}
            required
            fullWidth
            disabled={isReadOnly}
            size="small"
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <DatePicker
              label="Start Date"
              value={startDate ? dayjs(startDate) : null}
              onChange={(val) => setStartDate(val ? val.format('YYYY-MM-DD') : '')}
              disabled={isReadOnly}
              slotProps={{ textField: { size: 'small', fullWidth: true, required: true } }}
            />
            <DatePicker
              label="End Date"
              value={endDate ? dayjs(endDate) : null}
              onChange={(val) => setEndDate(val ? val.format('YYYY-MM-DD') : '')}
              disabled={isReadOnly}
              minDate={startDate ? dayjs(startDate) : undefined}
              slotProps={{ textField: { size: 'small', fullWidth: true, required: true } }}
            />
          </Box>

          {overlapWarning && (
            <Alert severity="warning" sx={{ py: 0.5 }}>{overlapWarning}</Alert>
          )}

          {startDate && endDate && (
            <Typography variant="caption" color="text.secondary">
              {businessDays} business day{businessDays !== 1 ? 's' : ''} in this sprint
              {teamOutcomeIds.length === 0 && ' · Link outcomes to the team to see allocation-adjusted availability'}
            </Typography>
          )}

          <Divider />

          <Typography variant="subtitle2">Member Capacity</Typography>
          {memberRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No active members on this team. Add members via Team settings first.
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Member</TableCell>
                    <TableCell align="center">Type</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        Available Days
                        <Tooltip title="Available days = business days × allocation % to linked outcomes. Hover a row's value for the full calculation." arrow>
                          <InfoIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="center">Planned Days</TableCell>
                    <TableCell align="center">Allocation</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedMemberRows.map((row) => {
                    const alloc = memberAllocations.get(row.roster_member_id) || { pct: 100, hasData: false };
                    const isOverride = Boolean(row._allocationOverride);
                    const tooltipAlloc = isOverride ? { ...alloc, pct: row.allocation_pct } : alloc;
                    const tooltipText = buildTooltip(businessDays, tooltipAlloc, isOverride, alloc.pct);
                    const isOverPlanned = row.planned_days > Math.ceil(row.available_days) && row.available_days > 0;

                    return (
                      <TableRow
                        key={row.roster_member_id}
                        sx={{
                          opacity: row.archived_at_sprint ? 0.45 : 1,
                          borderLeft: isOverride ? '3px solid' : '3px solid transparent',
                          borderColor: isOverride ? 'warning.main' : 'transparent',
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2">{row.member_name || row.roster_member_id}</Typography>
                        </TableCell>

                        <TableCell align="center">
                          <Chip
                            label={row.member_type === 'qa' ? 'QA' : 'Dev'}
                            size="small"
                            color={row.member_type === 'qa' ? 'secondary' : 'primary'}
                            variant="outlined"
                            sx={{ fontSize: '0.6rem', height: 18 }}
                          />
                        </TableCell>

                        <TableCell align="center">
                          {row.archived_at_sprint ? (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          ) : (
                            <Tooltip
                              title={
                                <Box sx={{ whiteSpace: 'pre-line', fontSize: '0.75rem' }}>
                                  {tooltipText}
                                </Box>
                              }
                              arrow
                              placement="top"
                            >
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  cursor: 'help',
                                  borderBottom: alloc.hasData ? '1px dashed' : 'none',
                                  borderColor: 'text.secondary',
                                }}
                              >
                                <Typography variant="body2" fontWeight={alloc.hasData ? 600 : 400}>
                                  {row.available_days}
                                </Typography>
                                {!alloc.hasData && startDate && (
                                  <InfoIcon sx={{ fontSize: 12, color: 'text.disabled' }} />
                                )}
                              </Box>
                            </Tooltip>
                          )}
                        </TableCell>

                        <TableCell align="center">
                          {row.archived_at_sprint ? (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          ) : (
                            <TextField
                              type="number"
                              value={row.planned_days}
                              onChange={(e) => {
                                const v = Math.max(0, parseInt(e.target.value, 10) || 0);
                                updateRow(row.roster_member_id, 'planned_days', v);
                              }}
                              inputProps={{
                                min: 0,
                                max: Math.ceil(row.available_days),
                                style: { textAlign: 'center', width: 60 },
                              }}
                              size="small"
                              variant="outlined"
                              disabled={isReadOnly}
                              error={isOverPlanned}
                            />
                          )}
                        </TableCell>

                        <TableCell align="center">
                          {row.archived_at_sprint ? null : (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                              <TextField
                                type="number"
                                value={Math.round(row.allocation_pct ?? 100)}
                                onChange={(e) => updateAllocationOverride(row.roster_member_id, e.target.value)}
                                inputProps={{ min: 0, max: 100, style: { textAlign: 'center', width: 44, padding: '4px 2px' } }}
                                InputProps={{
                                  endAdornment: <Typography variant="caption" sx={{ mr: 0.25 }}>%</Typography>,
                                }}
                                size="small"
                                variant="outlined"
                                disabled={isReadOnly}
                                sx={{
                                  '& .MuiOutlinedInput-root': isOverride
                                    ? { borderColor: 'warning.main', '& fieldset': { borderColor: 'warning.main' } }
                                    : {},
                                }}
                              />
                              {isOverride && !isReadOnly && (
                                <Tooltip title={`Reset to auto (${alloc.pct.toFixed(0)}%)`} arrow>
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    onClick={() => resetAllocationOverride(row.roster_member_id)}
                                    sx={{ p: 0.25 }}
                                  >
                                    <RefreshIcon sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          )}
                        </TableCell>

                        <TableCell align="center">
                          {row.archived_at_sprint ? (
                            <Chip label="Archived" size="small" variant="outlined" color="default" sx={{ fontSize: '0.6rem', height: 18 }} />
                          ) : (
                            <Chip label="Active" size="small" variant="outlined" color="success" sx={{ fontSize: '0.6rem', height: 18 }} />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {/* Totals footer */}
                  <TableRow sx={{ borderTop: '2px solid', borderColor: 'divider' }}>
                    <TableCell><Typography variant="body2" fontWeight={600}>Dev Total</Typography></TableCell>
                    <TableCell align="center" />
                    <TableCell align="center" />
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600}>{totalDevDays}</Typography>
                    </TableCell>
                    <TableCell align="center" />
                    <TableCell align="center" />
                  </TableRow>
                  <TableRow>
                    <TableCell><Typography variant="body2" fontWeight={600}>QA Total</Typography></TableCell>
                    <TableCell align="center" />
                    <TableCell align="center" />
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600}>{totalQaDays}</Typography>
                    </TableCell>
                    <TableCell align="center" />
                    <TableCell align="center" />
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          )}

          {velocity && (
            <Box sx={{ display: 'flex', gap: 2, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              {/* Dev section */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Dev Velocity</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" component="span" fontWeight={600}>
                      {velocity.dev?.rate.toFixed(2)} SP/day
                    </Typography>
                    <Chip
                      label={velocity.dev?.basis || velocity.basis}
                      size="small"
                      sx={{ fontSize: '0.55rem', height: 16 }}
                      color={(velocity.dev?.basis || velocity.basis) === 'historical' ? 'default' : (velocity.dev?.basis || velocity.basis) === 'blended' ? 'warning' : 'success'}
                      variant="outlined"
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Dev Days</Typography>
                  <Typography variant="body2" fontWeight={600}>{totalDevDays}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Expected Dev SP</Typography>
                  <Typography variant="body2" fontWeight={600}>{expectedSpDev}</Typography>
                </Box>
              </Box>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              {/* QA section */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">QA Velocity</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="body2" component="span" fontWeight={600}>
                      {velocity.qa?.rate.toFixed(2)} SP/day
                    </Typography>
                    <Chip
                      label={velocity.qa?.basis || velocity.basis}
                      size="small"
                      sx={{ fontSize: '0.55rem', height: 16 }}
                      color={(velocity.qa?.basis || velocity.basis) === 'historical' ? 'default' : (velocity.qa?.basis || velocity.basis) === 'blended' ? 'warning' : 'success'}
                      variant="outlined"
                    />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">QA Days</Typography>
                  <Typography variant="body2" fontWeight={600}>{totalQaDays}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Expected QA SP</Typography>
                  <Typography variant="body2" fontWeight={600}>{expectedSpQa}</Typography>
                </Box>
              </Box>
            </Box>
          )}

          <Divider />

          <FormControlLabel
            control={
              <Checkbox
                checked={markCompleted}
                onChange={(e) => setMarkCompleted(e.target.checked)}
                disabled={isReadOnly}
                size="small"
              />
            }
            label={<Typography variant="body2">Mark sprint as completed</Typography>}
          />

          {markCompleted && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Actual Dev Story Points"
                type="number"
                value={actualSpDev}
                onChange={(e) => setActualSpDev(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                fullWidth
                disabled={isReadOnly}
                size="small"
                helperText="Story points completed by Dev members"
              />
              <TextField
                label="Actual QA Story Points"
                type="number"
                value={actualSpQa}
                onChange={(e) => setActualSpQa(e.target.value)}
                inputProps={{ min: 0, step: 1 }}
                fullWidth
                disabled={isReadOnly}
                size="small"
                helperText="Story points completed by QA members"
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', pt: 1 }}>
            <Button variant="outlined" onClick={onCancel} disabled={saving}>Cancel</Button>
            {!isReadOnly && (
              <Button variant="contained" onClick={handleSave} disabled={saving}>
                {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                {isEdit ? 'Save Sprint' : 'Create Sprint'}
              </Button>
            )}
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
}
